import { useEffect, useRef, useState } from "react";
import { api, resolveMediaUrl, formatApiErrorDetail } from "@/lib/api";
import { Loader2, Plus, Trash2, Save, User2, X } from "lucide-react";

const empty = {
    id: null,
    name: "",
    role: "",
    description: "",
    avatar_url: "",
    order: 0,
};

export default function TeamManager() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [draft, setDraft] = useState(empty);
    const fileRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/team");
            setItems(data);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const reset = () => {
        setDraft(empty);
        if (fileRef.current) fileRef.current.value = "";
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!draft.name.trim()) {
            setError("Imię / nazwa jest wymagane.");
            return;
        }
        setSubmitting(true);
        setError("");
        try {
            let payload = { ...draft };
            const file = fileRef.current?.files?.[0];
            if (file) {
                const fd = new FormData();
                fd.append("file", file);
                const up = await api.post("/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                payload.avatar_url = up.data.url;
            }
            if (draft.id) {
                const { id, ...patch } = payload;
                await api.put(`/team/${id}`, patch);
            } else {
                const { id, ...newMember } = payload;
                await api.post("/team", newMember);
            }
            reset();
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSubmitting(false);
        }
    };

    const onEdit = (member) => {
        setDraft({
            id: member.id,
            name: member.name || "",
            role: member.role || "",
            description: member.description || "",
            avatar_url: member.avatar_url || "",
            order: member.order ?? 0,
        });
        if (fileRef.current) fileRef.current.value = "";
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const onDelete = async (id) => {
        if (!window.confirm("Usunąć osobę z zespołu?")) return;
        try {
            await api.delete(`/team/${id}`);
            if (draft.id === id) reset();
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    return (
        <div data-testid="admin-team" className="grid lg:grid-cols-12 gap-8">
            <form
                onSubmit={onSubmit}
                data-testid="team-form"
                className="panel-yellow p-6 lg:col-span-5 h-fit"
            >
                <div className="flex items-center justify-between mb-3">
                    <div className="label-eyebrow">
                        {draft.id ? "Edytuj osobę" : "Dodaj osobę"}
                    </div>
                    {draft.id && (
                        <button
                            type="button"
                            data-testid="team-cancel-edit"
                            onClick={reset}
                            className="text-xs text-[color:var(--text-secondary)] hover:text-white inline-flex items-center gap-1"
                        >
                            <X size={12} /> Anuluj
                        </button>
                    )}
                </div>

                <Field label="Imię / nazwa *">
                    <input
                        data-testid="team-name-input"
                        value={draft.name}
                        onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                        required
                        className="input"
                    />
                </Field>

                <Field label="Rola (opcjonalna)">
                    <input
                        data-testid="team-role-input"
                        value={draft.role}
                        onChange={(e) => setDraft((d) => ({ ...d, role: e.target.value }))}
                        placeholder="np. Mapowanie, Testy, 3D"
                        className="input"
                    />
                </Field>

                <Field label="Opis (czym się zajmuje)">
                    <textarea
                        data-testid="team-description-input"
                        value={draft.description}
                        onChange={(e) =>
                            setDraft((d) => ({ ...d, description: e.target.value }))
                        }
                        rows={4}
                        className="input"
                    />
                </Field>

                <Field label="Kolejność">
                    <input
                        type="number"
                        data-testid="team-order-input"
                        value={draft.order}
                        onChange={(e) =>
                            setDraft((d) => ({
                                ...d,
                                order: parseInt(e.target.value || "0", 10),
                            }))
                        }
                        className="input"
                    />
                </Field>

                <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                        Avatar (opcjonalny)
                    </div>
                    <div className="flex items-center gap-3 mb-2">
                        {draft.avatar_url && (
                            <img
                                src={resolveMediaUrl(draft.avatar_url)}
                                alt="avatar"
                                className="w-12 h-12 rounded-full object-cover border border-white/10"
                            />
                        )}
                        <input
                            data-testid="team-avatar-url-input"
                            value={draft.avatar_url}
                            onChange={(e) =>
                                setDraft((d) => ({ ...d, avatar_url: e.target.value }))
                            }
                            placeholder="URL avatara"
                            className="input"
                        />
                    </div>
                    <input
                        type="file"
                        accept="image/*"
                        ref={fileRef}
                        data-testid="team-avatar-file-input"
                        className="text-sm text-[color:var(--text-secondary)] file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-[color:var(--primary)] file:text-[color:var(--bg-base)] file:text-xs file:uppercase file:tracking-wider file:font-semibold mb-3"
                    />
                </div>

                {error && (
                    <div data-testid="team-error" className="text-sm text-red-400 mb-3">
                        {error}
                    </div>
                )}

                <button
                    data-testid="team-submit-btn"
                    type="submit"
                    disabled={submitting}
                    className="btn-primary w-full mt-2 disabled:opacity-60"
                >
                    {submitting ? (
                        <Loader2 className="animate-spin" size={14} />
                    ) : draft.id ? (
                        <Save size={14} />
                    ) : (
                        <Plus size={14} />
                    )}
                    {draft.id ? "Zapisz zmiany" : "Dodaj osobę"}
                </button>
                <style>{`
                    .input { width:100%; background: var(--bg-base); border:1px solid rgba(255,255,255,0.1); border-radius:6px; padding:8px 12px; color:white; font-size:14px; }
                    .input:focus { outline:none; border-color: var(--primary); }
                `}</style>
            </form>

            <div className="lg:col-span-7">
                <div className="label-eyebrow mb-4">Zespół ({items.length})</div>
                {loading ? (
                    <Loader2 className="animate-spin text-[color:var(--text-secondary)]" />
                ) : items.length === 0 ? (
                    <div className="panel-yellow p-8 text-center text-sm text-[color:var(--text-secondary)]">
                        <User2 size={24} className="mx-auto mb-3 text-[color:var(--primary)]" />
                        Brak osób w zespole.
                    </div>
                ) : (
                    <ul className="space-y-3">
                        {items.map((m) => (
                            <li
                                key={m.id}
                                data-testid={`team-row-${m.id}`}
                                className="panel-yellow p-4 flex items-start gap-4"
                            >
                                <div className="w-12 h-12 rounded-full overflow-hidden bg-[color:var(--bg-base)] border border-white/10 flex items-center justify-center shrink-0">
                                    {m.avatar_url ? (
                                        <img
                                            src={resolveMediaUrl(m.avatar_url)}
                                            alt={m.name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <User2
                                            size={18}
                                            className="text-[color:var(--primary)]"
                                        />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-baseline gap-2">
                                        <span className="text-white font-medium">{m.name}</span>
                                        {m.role && (
                                            <span className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--primary)]">
                                                {m.role}
                                            </span>
                                        )}
                                        <span className="text-[10px] text-[color:var(--text-muted)]">
                                            #{m.order ?? 0}
                                        </span>
                                    </div>
                                    {m.description && (
                                        <p className="text-xs text-[color:var(--text-secondary)] mt-1 line-clamp-2 whitespace-pre-wrap">
                                            {m.description}
                                        </p>
                                    )}
                                </div>
                                <button
                                    data-testid={`team-edit-${m.id}`}
                                    onClick={() => onEdit(m)}
                                    className="text-xs uppercase tracking-wider text-[color:var(--text-secondary)] hover:text-white px-2"
                                >
                                    Edytuj
                                </button>
                                <button
                                    data-testid={`team-delete-${m.id}`}
                                    onClick={() => onDelete(m.id)}
                                    className="text-red-400 hover:text-red-300 p-2"
                                    aria-label="Usuń"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block mb-3">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                {label}
            </span>
            {children}
        </label>
    );
}
